/** Shimmer placeholders matching listing card layouts (services, packages, offers, news). Uses `animate-shimmer` from globals.css. */

function Sk({ className }: { className: string }) {
  return <div className={`animate-shimmer rounded ${className}`} aria-hidden />;
}

/** Services listing — image h-44, duration chip, title, description, price + CTA */
export function ServiceListingCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-[#E5E5E5] bg-white">
      <div className="relative h-44 shrink-0 overflow-hidden bg-[#F4F4F4]">
        <Sk className="absolute inset-0 rounded-none" />
        <div className="absolute start-3 top-3 z-[1]">
          <Sk className="h-7 w-[5.5rem] rounded-md" />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <Sk className="mb-2 h-4 w-[92%]" />
        <Sk className="mb-1 h-4 w-[72%]" />
        <Sk className="mb-4 mt-2 h-3 w-full" />
        <Sk className="mb-4 h-3 w-[88%]" />
        <div className="mt-auto flex min-h-[2.5rem] items-center gap-2">
          <div className="flex shrink-0 flex-col gap-1">
            <Sk className="h-3 w-16" />
            <Sk className="h-6 w-24" />
          </div>
          <div className="flex min-w-0 flex-1 justify-end">
            <Sk className="h-[42px] w-[min(100%,9rem)] rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Packages — image h-44, category + save badges, title, description, price + book */
export function PackageListingCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-[#E5E5E5] bg-white">
      <div className="relative isolate z-0 h-44 shrink-0 overflow-hidden bg-[#F4F4F4]">
        <Sk className="absolute inset-0 rounded-none" />
        <div className="absolute start-3 top-3 z-[1]">
          <Sk className="h-6 w-20 rounded-md" />
        </div>
        <div className="absolute end-3 top-3 z-[1]">
          <Sk className="h-6 w-14 rounded-md" />
        </div>
      </div>
      <div className="relative z-10 flex flex-1 flex-col bg-white p-4">
        <Sk className="mb-2 h-4 w-[90%]" />
        <Sk className="mb-1 h-4 w-[65%]" />
        <Sk className="mb-4 mt-2 h-3 w-full min-h-[2.5rem]" />
        <Sk className="mb-4 h-3 w-[85%]" />
        <div className="mt-2 flex min-h-[2.5rem] items-center gap-2">
          <div className="min-w-0 shrink">
            <Sk className="h-7 w-28" />
          </div>
          <div className="flex min-w-0 flex-1 justify-end">
            <Sk className="h-8 w-[7.5rem] rounded-md sm:h-9 sm:w-[8.5rem]" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Offers — aspect-4/3 image, validity + optional badges, marquee strip, body + dual CTAs */
export function OfferListingCardSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-[#E5E5E5] bg-white">
      <div className="relative aspect-4/3 w-full shrink-0 bg-[#F4F4F4]">
        <Sk className="absolute inset-0 rounded-none" />
        <div className="absolute start-3 top-3 z-[2]">
          <Sk className="h-6 w-28" />
        </div>
        <div className="absolute end-3 top-3 z-[2]">
          <Sk className="h-7 w-16 rounded" />
        </div>
        <div className="absolute inset-x-0 bottom-0 z-[1] h-9 overflow-hidden">
          <Sk className="h-full w-full rounded-none opacity-90" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <Sk className="mb-2 h-4 w-[94%]" />
        <Sk className="mb-1 h-4 w-[58%]" />
        <Sk className="mb-3 mt-1 min-h-[2.5rem] w-full" />
        <Sk className="mb-3 h-3 w-[92%]" />
        <Sk className="mb-3 h-6 w-36 rounded-md" />
        <div className="mt-auto flex gap-2">
          <Sk className="h-[42px] flex-1 rounded-lg" />
          <Sk className="h-[42px] flex-1 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/** News — h-48 image + category, date, title, excerpt, read-more row */
export function NewsListingCardSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-[#E5E5E5] bg-white">
      <div className="relative isolate z-0 h-48 shrink-0 overflow-hidden bg-[#E5E5E5]">
        <Sk className="absolute inset-0 rounded-none" />
        <div className="absolute start-3 top-3 z-[1]">
          <Sk className="h-6 w-24 rounded-md" />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <Sk className="mb-1 h-3 w-28" />
        <Sk className="mt-1 mb-2 h-5 w-full" />
        <Sk className="mb-2 h-5 w-[92%]" />
        <div className="min-h-[2.75rem] flex-1 space-y-1.5 pt-1">
          <Sk className="h-3 w-full" />
          <Sk className="h-3 w-full" />
          <Sk className="h-3 w-[70%]" />
        </div>
        <div className="mt-auto border-t border-[#E5E5E5] pt-4">
          <Sk className="h-4 w-28" />
        </div>
      </div>
    </div>
  );
}

export function ServiceListingSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3"
      aria-busy="true"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, i) => (
        <ServiceListingCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function PackageListingSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 md:mt-0 mt-5"
      aria-busy="true"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, i) => (
        <PackageListingCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function OfferListingSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 sm:mt-0 mt-5"
      aria-busy="true"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, i) => (
        <OfferListingCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function NewsListingSkeletonGrid({ count = 9 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, i) => (
        <NewsListingCardSkeleton key={i} />
      ))}
    </div>
  );
}
